import { env } from '@config/env';
import type { DonationInitiateRequest, DonationInitiateResponse, ApiError } from '@/types';

const API_URL = env.REACT_APP_API_URL;

export class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    timeout = 30000
  ): Promise<T> {
    const url = `${API_URL}${endpoint}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        const error = data as ApiError;
        throw new Error(error.error || 'Request failed');
      }

      return data as T;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError();
      }
      throw error;
    }
  }

  async initiateDonation(
    data: DonationInitiateRequest,
    timeout = 15000
  ): Promise<DonationInitiateResponse> {
    return this.request<DonationInitiateResponse>(
      '/donate/initiate',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      timeout
    );
  }
}

export const apiService = new ApiService();
